import { z } from 'zod';
import { IdSchema, TimestampSchema, PaginationSchema, PageSchema } from './common.js';

/** 替换类型 */
export const ReplaceKindSchema = z.enum(['object', 'scene', 'person']);
export type ReplaceKind = z.infer<typeof ReplaceKindSchema>;

/** 批次状态 */
export const BatchStatusSchema = z.enum(['draft', 'processing', 'done', 'failed']);
export type BatchStatus = z.infer<typeof BatchStatusSchema>;

/** 提示词修改策略：按替换类型不同选项不同（详见 01-ux-spec.md §5.3） */
export const ObjectStrategySchema = z.enum(['完全替换', '保持轮廓', '保持材质', '智能融合']);
export const SceneStrategySchema = z.enum(['整体替换', '仅替换天空', '保留前景主体', '风格迁移']);
export const PersonStrategySchema = z.enum(['完全替换', '仅替换服饰', '仅替换面部', '保留动作姿态']);
export type ObjectStrategy = z.infer<typeof ObjectStrategySchema>;
export type SceneStrategy = z.infer<typeof SceneStrategySchema>;
export type PersonStrategy = z.infer<typeof PersonStrategySchema>;
export type ReplaceStrategy = ObjectStrategy | SceneStrategy | PersonStrategy;

/** 批次任务参数（生成相关） */
export const BatchParamsSchema = z.object({
  resolution: z.enum(['720p', '1080p', '2K']),
  duration: z.enum(['5 秒', '10 秒', '15 秒']),
  concurrency: z.number().int().min(1).max(16),
  retries: z.number().int().min(1).max(5),
  /** Slice 12：物体替换的「要替换的物体」文字描述，给 SAM3 自动分割用（如 "treadmill"）。
   *  仅 object 真·替换路线用；留空则该路线回退为整体重生成。建议用英文更准。 */
  replaceTarget: z.string().max(120).optional(),
});
export type BatchParams = z.infer<typeof BatchParamsSchema>;

/** Job 分布（一个批次下子任务的完成/进行中/失败计数） */
export const JobsDistSchema = z.object({
  done: z.number().int().nonnegative(),
  running: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type JobsDist = z.infer<typeof JobsDistSchema>;

/** 批次任务（库里存的样子，前端也用同一份展示） */
export const BatchTaskSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(120),
  kind: ReplaceKindSchema,
  status: BatchStatusSchema,
  sourceAssetId: IdSchema,
  referenceAssetId: IdSchema,
  prompt: z.string(),
  strategy: z.string(), // 运行时用 ObjectStrategy|Scene|Person 各自校验
  params: BatchParamsSchema,
  outputLocation: z.string(),
  jobs: JobsDistSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  /** 完成后给的下载链接 */
  outputUrl: z.string().url().optional(),
  /** 失败时给的提示 */
  errorMessage: z.string().optional(),
});
export type BatchTask = z.infer<typeof BatchTaskSchema>;

/** 列表请求 */
export const ListBatchesRequestSchema = z.object({
  kind: ReplaceKindSchema.optional(),
  status: BatchStatusSchema.optional(),
  search: z.string().optional(),
  outputLocation: z.string().optional(),
  createdFrom: TimestampSchema.optional(),
  createdTo: TimestampSchema.optional(),
}).merge(PaginationSchema);
export type ListBatchesRequest = z.infer<typeof ListBatchesRequestSchema>;

export const ListBatchesResponseSchema = PageSchema(BatchTaskSchema);
export type ListBatchesResponse = z.infer<typeof ListBatchesResponseSchema>;

/** 详情 */
export const GetBatchRequestSchema = z.object({ id: IdSchema });
export type GetBatchRequest = z.infer<typeof GetBatchRequestSchema>;

export const GetBatchResponseSchema = BatchTaskSchema;
export type GetBatchResponse = z.infer<typeof GetBatchResponseSchema>;

/** 创建（保存草稿 / 提交执行） */
export const CreateBatchRequestSchema = z.object({
  name: z.string().min(1).max(120),
  kind: ReplaceKindSchema,
  sourceAssetId: IdSchema,
  referenceAssetId: IdSchema,
  prompt: z.string().min(1),
  strategy: z.string().min(1),
  params: BatchParamsSchema,
  outputLocation: z.string().min(1),
  /** draft = 不调模型；processing = 立刻提交 */
  intent: z.enum(['draft', 'submit']),
});
export type CreateBatchRequest = z.infer<typeof CreateBatchRequestSchema>;

export const CreateBatchResponseSchema = BatchTaskSchema;
export type CreateBatchResponse = z.infer<typeof CreateBatchResponseSchema>;

/** 重试 */
export const RetryBatchRequestSchema = z.object({ id: IdSchema });
export type RetryBatchRequest = z.infer<typeof RetryBatchRequestSchema>;
export const RetryBatchResponseSchema = BatchTaskSchema;
export type RetryBatchResponse = z.infer<typeof RetryBatchResponseSchema>;

/** 取消 */
export const CancelBatchRequestSchema = z.object({ id: IdSchema });
export type CancelBatchRequest = z.infer<typeof CancelBatchRequestSchema>;
export const CancelBatchResponseSchema = BatchTaskSchema;
export type CancelBatchResponse = z.infer<typeof CancelBatchResponseSchema>;

/** 仪表盘概览（任务管理首屏用） */
export const BatchOverviewResponseSchema = z.object({
  runningSubJobs: z.number().int().nonnegative(),
  recentlyDoneBatches: z.number().int().nonnegative(),
  running: z.array(BatchTaskSchema),
  recent: z.array(BatchTaskSchema),
});
export type BatchOverviewResponse = z.infer<typeof BatchOverviewResponseSchema>;
